import type {
  MetricsReportParams,
  MetricsReportSlug,
  ReportFileView,
} from "@/types/metricsReports";
import { apiUrl } from "@/utils/url";
import { getErrorMessage } from "../errors";
import { isAbortError } from "../errors/isAbortError";
import { FILE_TRANSFER_TIMEOUT_MS, HttpClient, HttpError } from "../HttpClient";
import type { IMetricsReportsService } from "./IMetricsReportsService";

const TIMEOUT_MESSAGE =
  "O relatório está a demorar mais do que o esperado. Tente novamente dentro de instantes.";

const RATE_LIMIT_MESSAGE =
  "Há demasiados relatórios a serem gerados neste momento. Aguarde um pouco e tente novamente.";

const INVALID_FILTERS_MESSAGE =
  "Não foi possível gerar o relatório com estes filtros. Confirme o período e a loja.";

const FALLBACK_MESSAGE =
  "Não foi possível gerar o relatório. Atualize a página e tente novamente.";

/**
 * The query string, with absent parameters genuinely absent.
 *
 * `storeId` missing is what asks for every store, so it must be left out
 * rather than sent empty or as a zero; `startDate` is missing only for
 * `client-without-appointments`, which is defined without it.
 */
const buildReportQuery = (params: MetricsReportParams): URLSearchParams => {
  const query = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    query.set(key, String(value));
  }

  return query;
};

export class MetricsReportsService implements IMetricsReportsService {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = new HttpClient({
      baseURL: apiUrl,
      /*
       * Generating a report is slow enough that the API documents a 503 for
       * it, and the answer then carries the whole file, so it gets the file
       * transfer timeout rather than the ten-second default.
       */
      timeout: FILE_TRANSFER_TIMEOUT_MS,
      headers: {},
    });
  }

  /**
   * Translates the failure before it reaches the page.
   *
   * Two reasons this cannot lean on the shared helpers. The API types its 400,
   * 429 and 503 bodies with the same `ReportFileView` as a success, so none of
   * them carries an `errorCode` for `getErrorMessage` to read; and
   * `HttpClient`'s own status messages call a 429 "confirme os dados" and a
   * 503 "não foi possível comunicar com o servidor", neither of which tells
   * the user the one thing that is true — wait and try again.
   */
  private translate(error: unknown, signal?: AbortSignal): Error {
    /*
     * `HttpClient` rejects its own timeout as an HttpError 408, so an abort
     * here is the caller cancelling and passes through silently. An abort the
     * caller did not ask for still reads as the report running out of time.
     */
    if (isAbortError(error)) {
      if (signal?.aborted) return error as Error;

      return new Error(TIMEOUT_MESSAGE);
    }

    if (error instanceof HttpError) {
      if (error.status === 408) return new Error(TIMEOUT_MESSAGE);
      if (error.status === 429) return new Error(RATE_LIMIT_MESSAGE);
      if (error.status === 503) return new Error(TIMEOUT_MESSAGE);
      if (error.status === 400) return new Error(INVALID_FILTERS_MESSAGE);
    }

    return new Error(getErrorMessage(error, FALLBACK_MESSAGE));
  }

  async getReport(
    slug: MetricsReportSlug,
    params: MetricsReportParams,
    signal?: AbortSignal,
  ): Promise<ReportFileView> {
    const query = buildReportQuery(params);

    try {
      return await this.httpClient.get<ReportFileView>(
        `/metrics/reports/${slug}?${query.toString()}`,
        signal ? { signal } : {},
      );
    } catch (error) {
      throw this.translate(error, signal);
    }
  }
}
